'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import type { EmblaOptionsType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useImperativeHandle } from 'react';
type AutoplayOptions = {
  delay?: number;
  stopOnInteraction?: boolean;
};
interface HorizontalScrollProps {
  children: ReactNode;
  className?: string;
  slideClassName?: string;
  /** 슬라이드 변경 시 콜백 */
  onSlideChange?: (index: number) => void;
  /** 스크롤 위치(0-1)와 콘텐츠 중 보이는 비율(0-1) 콜백 */
  onScrollMetrics?: (metrics: {
    progress: number;
    visibleRatio: number;
  }) => void;
  /** 무한 루프 활성화 */
  loop?: boolean;
  /** 자동 재생 옵션 */
  autoplay?: boolean | AutoplayOptions;
  /** 슬라이드 정렬 */
  align?: 'start' | 'center' | 'end';
  /** 드래그 자유도 */
  dragFree?: boolean;
  /** API 접근을 위한 ref */
  carouselRef?: React.RefObject<CarouselApi | null>;
}
export interface CarouselApi {
  scrollNext: () => void;
  scrollPrev: () => void;
  scrollTo: (index: number) => void;
  selectedScrollSnap: () => number;
  canScrollNext: () => boolean;
  canScrollPrev: () => boolean;
}
export const HorizontalScroll = t0 => {
  const $ = _c(37);
  const {
    children,
    className,
    slideClassName,
    onSlideChange,
    onScrollMetrics,
    loop: t1,
    autoplay: t2,
    align: t3,
    dragFree: t4,
    carouselRef
  } = t0;
  const loop = t1 === undefined ? false : t1;
  const autoplay = t2 === undefined ? false : t2;
  const align = t3 === undefined ? "start" : t3;
  const dragFree = t4 === undefined ? true : t4;
  const t5 = !loop && dragFree;
  const t6 = loop ? false : "trimSnaps";
  let t7;
  if ($[0] !== align || $[1] !== loop || $[2] !== t5 || $[3] !== t6) {
    t7 = {
      loop,
      align,
      dragFree: t5,
      containScroll: t6
    };
    $[0] = align;
    $[1] = loop;
    $[2] = t5;
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const options = t7;
  let t8;
  if ($[5] !== autoplay) {
    t8 = autoplay ? [Autoplay(typeof autoplay === "object" ? autoplay : {
      delay: 5000,
      stopOnInteraction: false
    })] : [];
    $[5] = autoplay;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const plugins = t8;
  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);
  let t9;
  if ($[7] !== emblaApi) {
    t9 = () => {
      if (!emblaApi) {
        return;
      }
      return emblaApi.plugins()?.autoplay as ReturnType<typeof Autoplay> | undefined;
    };
    $[7] = emblaApi;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const getAutoplay = t9;
  let t10;
  if ($[9] !== getAutoplay) {
    t10 = () => {
      const autoplayPlugin = getAutoplay();
      autoplayPlugin?.reset?.();
    };
    $[9] = getAutoplay;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const resetAutoplay = t10;
  let t11;
  let t12;
  if ($[11] !== emblaApi || $[12] !== resetAutoplay) {
    t11 = () => {
      if (!emblaApi) {
        return {
          scrollNext: _temp,
          scrollPrev: _temp2,
          scrollTo: _temp3,
          selectedScrollSnap: _temp4,
          canScrollNext: _temp5,
          canScrollPrev: _temp6
        };
      }
      return {
        scrollNext: () => {
          emblaApi.scrollNext();
          resetAutoplay();
        },
        scrollPrev: () => {
          emblaApi.scrollPrev();
          resetAutoplay();
        },
        scrollTo: index => emblaApi.scrollTo(index),
        selectedScrollSnap: () => emblaApi.selectedScrollSnap(),
        canScrollNext: () => emblaApi.canScrollNext(),
        canScrollPrev: () => emblaApi.canScrollPrev()
      };
    };
    t12 = [emblaApi, resetAutoplay];
    $[11] = emblaApi;
    $[12] = resetAutoplay;
    $[13] = t11;
    $[14] = t12;
  } else {
    t11 = $[13];
    t12 = $[14];
  }
  useImperativeHandle(carouselRef, t11, t12);
  let t13;
  if ($[15] !== emblaApi || $[16] !== onSlideChange) {
    t13 = () => {
      if (!emblaApi) {
        return;
      }
      const index_0 = emblaApi.selectedScrollSnap();
      onSlideChange?.(index_0);
    };
    $[15] = emblaApi;
    $[16] = onSlideChange;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const onSelect = t13;
  let t14;
  if ($[18] !== emblaApi || $[19] !== onScrollMetrics) {
    t14 = () => {
      if (!emblaApi || !onScrollMetrics) {
        return;
      }
      const viewportWidth = emblaApi.rootNode().clientWidth;
      const contentWidth = emblaApi.containerNode().scrollWidth;
      onScrollMetrics({
        progress: emblaApi.scrollProgress(),
        visibleRatio: Math.min(1, viewportWidth / contentWidth)
      });
    };
    $[18] = emblaApi;
    $[19] = onScrollMetrics;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const onScroll = t14;
  let t15;
  let t16;
  if ($[21] !== emblaApi || $[22] !== onScroll || $[23] !== onSelect) {
    t15 = () => {
      if (!emblaApi) {
        return;
      }
      onSelect();
      onScroll();
      emblaApi.on("select", onSelect);
      emblaApi.on("scroll", onScroll);
      emblaApi.on("reInit", onScroll);
      return () => {
        emblaApi.off("select", onSelect);
        emblaApi.off("scroll", onScroll);
        emblaApi.off("reInit", onScroll);
      };
    };
    t16 = [emblaApi, onSelect, onScroll];
    $[21] = emblaApi;
    $[22] = onScroll;
    $[23] = onSelect;
    $[24] = t15;
    $[25] = t16;
  } else {
    t15 = $[24];
    t16 = $[25];
  }
  useEffect(t15, t16);
  let t17;
  if ($[26] !== className) {
    t17 = cn("overflow-hidden", className);
    $[26] = className;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== slideClassName) {
    t18 = cn("flex", slideClassName);
    $[28] = slideClassName;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== children || $[31] !== t18) {
    t19 = <div className={t18}>{children}</div>;
    $[30] = children;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== emblaRef || $[34] !== t17 || $[35] !== t19) {
    t20 = <div ref={emblaRef} className={t17}>{t19}</div>;
    $[33] = emblaRef;
    $[34] = t17;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  return t20;
};
HorizontalScroll.displayName = 'HorizontalScroll';
function _temp() {}
function _temp2() {}
function _temp3() {}
function _temp4() {
  return 0;
}
function _temp5() {
  return false;
}
function _temp6() {
  return false;
}